import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

export default function ModeratorView() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [filterPaid, setFilterPaid] = useState('all');

  useEffect(() => {
    api.request('GET', '/api/moderator/events', null, token)
      .then(d => {
        setEvents(d.events || []);
        if (d.events?.length === 1) setSelectedEvent(d.events[0]);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const event = selectedEvent;
  const attendees = (event?.attendees || []).filter(a => {
    const q = search.toLowerCase();
    const displayName = a.name || `${a.firstName || ''} ${a.lastName1 || ''}`.trim() || '';
    const matchQ = !q || displayName.toLowerCase().includes(q);
    const matchF = filterPaid === 'all' || (filterPaid === 'paid' ? a.paymentUpToDate : !a.paymentUpToDate);
    return matchQ && matchF;
  });

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1.4rem' }}>
        <div className="section-label">Moderación</div>
        <h1>Mis eventos asignados</h1>
        <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.9rem', marginTop: '.2rem' }}>
          Lista de asistentes bajo tu supervisión
        </p>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {events.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)', fontStyle: 'italic' }}>
          No tienes eventos asignados todavía.
        </div>
      )}

      {/* Selector de evento */}
      {events.length > 1 && (
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
          {events.map(ev => (
            <button
              key={ev.id}
              className={`btn btn-sm ${selectedEvent?.id === ev.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setSelectedEvent(ev); setSearch(''); setFilterPaid('all'); }}
              style={{ flex: '1 1 auto' }}
            >
              🏰 {ev.name} <span style={{ opacity: .7 }}>({ev.totalAttendees})</span>
            </button>
          ))}
        </div>
      )}

      {event && (
        <>
          {/* Info del evento */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ marginBottom: '.8rem' }}>
              <h2 style={{ marginBottom: '.3rem', wordBreak: 'break-word' }}>{event.name}</h2>
              <div style={{ fontSize: '.82rem', color: 'var(--mid)', lineHeight: 1.5 }}>
                📅 {new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              {event.location && (
                <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginTop: '.2rem', wordBreak: 'break-word' }}>
                  📍 {event.location}
                </div>
              )}
              {event.category === 'school' && event.schoolId && (
                <div style={{ fontSize: '.82rem', color: '#4a8fa8', marginTop: '.2rem' }}>
                  🏫 ID Colegio: <strong>{event.schoolId}</strong>
                </div>
              )}
              {event.password && (
                <div style={{ fontSize: '.82rem', color: '#f59e0b', marginTop: '.2rem' }}>
                  🔒 Contraseña: <strong>{event.password}</strong>
                </div>
              )}
            </div>

            {/* Contadores */}
            <div style={{ display: 'grid', gridTemplateColumns: event.requiresPayment ? 'repeat(3, 1fr)' : '1fr', gap: '.5rem' }}>
              <div style={{ textAlign: 'center', background: 'rgba(122,171,138,.08)', border: '1px solid rgba(122,171,138,.2)', borderRadius: 'var(--radius)', padding: '.6rem .5rem' }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.8rem', fontWeight: 700, color: 'var(--white)', lineHeight: 1 }}>{event.totalAttendees}</div>
                <div style={{ fontSize: '.6rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: '.25rem' }}>Asistentes</div>
              </div>
              {event.requiresPayment && (
                <>
                  <div style={{ textAlign: 'center', background: 'rgba(74,124,89,.08)', border: '1px solid rgba(74,124,89,.2)', borderRadius: 'var(--radius)', padding: '.6rem .5rem' }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.8rem', fontWeight: 700, color: 'var(--sage)', lineHeight: 1 }}>{event.paidCount}</div>
                    <div style={{ fontSize: '.6rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: '.25rem' }}>Al día</div>
                  </div>
                  <div style={{ textAlign: 'center', background: 'rgba(214,60,42,.06)', border: '1px solid rgba(214,60,42,.2)', borderRadius: 'var(--radius)', padding: '.6rem .5rem' }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.8rem', fontWeight: 700, color: '#ff7a6a', lineHeight: 1 }}>{event.totalAttendees - event.paidCount}</div>
                    <div style={{ fontSize: '.6rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: '.25rem' }}>Pendiente</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
            <input
              className="form-input"
              type="text"
              placeholder="Buscar por nombre..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: '1 1 180px', minWidth: 0 }}
            />
            {event.requiresPayment && (
              <select
                className="form-select"
                value={filterPaid}
                onChange={e => setFilterPaid(e.target.value)}
                style={{ flex: '1 1 140px', minWidth: 0 }}
              >
                <option value="all">Todos ({event.totalAttendees})</option>
                <option value="paid">Al día ({event.paidCount})</option>
                <option value="pending">Pendiente ({event.totalAttendees - event.paidCount})</option>
              </select>
            )}
            <span style={{ fontSize: '.78rem', color: 'var(--mid)', whiteSpace: 'nowrap' }}>
              {attendees.length} resultado{attendees.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Lista de asistentes — tarjetas en móvil, tabla en desktop */}
          {attendees.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)', fontStyle: 'italic' }}>
              Sin resultados
            </div>
          )}

          {/* DESKTOP: tabla */}
          <div className="mod-table-desktop">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th>Nombre completo</th>
                      <th>Edad / Rol</th>
                      {event.requiresPayment && <th>Pago</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {attendees.map((a, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--mid)', fontSize: '.75rem' }}>{i + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: '.9rem' }}>
                            {a.lastName1 && a.lastName2
                              ? `${a.lastName1} ${a.lastName2}, ${a.firstName}`
                              : a.name}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                            {a.age && <span style={{ fontSize: '.8rem', color: 'var(--mid)' }}>{a.age} años</span>}
                            <span className={`badge ${a.role === 'child' ? 'badge-amber' : 'badge-stone'}`}>
                              {a.role === 'child' ? 'Menor' : 'Adulto'}
                            </span>
                          </div>
                        </td>
                        {event.requiresPayment && (
                          <td>
                            {a.paymentUpToDate
                              ? <span className="badge badge-green">✓ Al día</span>
                              : <span className="badge badge-danger">⚠ Pendiente</span>
                            }
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* MÓVIL: tarjetas */}
          <div className="mod-table-mobile" style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {attendees.map((a, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(122,171,138,.12)',
                borderRadius: 'var(--radius)',
                padding: '.75rem 1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '.8rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', minWidth: 0 }}>
                  <span style={{ fontSize: '.7rem', color: 'var(--mid)', flexShrink: 0, width: 20, textAlign: 'right' }}>{i + 1}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.88rem', wordBreak: 'break-word' }}>
                      {a.lastName1 && a.lastName2
                        ? `${a.lastName1} ${a.lastName2}, ${a.firstName}`
                        : a.name}
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center', marginTop: '.25rem', flexWrap: 'wrap' }}>
                      {a.age && <span style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{a.age} años</span>}
                      <span className={`badge ${a.role === 'child' ? 'badge-amber' : 'badge-stone'}`} style={{ fontSize: '.6rem' }}>
                        {a.role === 'child' ? 'Menor' : 'Adulto'}
                      </span>
                    </div>
                  </div>
                </div>
                {event.requiresPayment && (
                  <div style={{ flexShrink: 0 }}>
                    {a.paymentUpToDate
                      ? <span className="badge badge-green" style={{ fontSize: '.6rem' }}>✓ Al día</span>
                      : <span className="badge badge-danger" style={{ fontSize: '.6rem' }}>⚠ Pdte.</span>
                    }
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}